/**
 * The mesh's logs: the configuration log (every mesh change in
 * words, from the audit trail) and the network flow log (who talked to whom). A node's recent
 * activity is the same configuration log filtered to it.
 */

import type { MeshFlowView, MeshLogEntry, MeshLogResponse } from '@aioe/shared';
import { dateTime } from '@aioe/shared/wording/dateTime';
import { Segment } from '@heroui-pro/react';
import { useState } from 'react';

import { routes } from '../api/client.ts';
import { timeAgo, useLoad } from '../hooks.ts';
import { bytesInWords } from './meshWords.ts';
import { ChoiceSelect, ErrorNote, ListBlock, ListGroup, StatusMark, type ArtTone } from './ui.tsx';

const list = (v: unknown): string => (Array.isArray(v) && v.length ? v.map(String).join(', ') : 'no tags');
const text = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined);

/** One audit row of the mesh, as a sentence. */
export function meshEntryInWords(e: MeshLogEntry, log: Pick<MeshLogResponse, 'nodes' | 'actors'>): { who: string; what: string } {
  const d = e.detail ?? {};
  const node = (e.target && (log.nodes[e.target] ?? text(d.name))) || 'a machine';
  const who = e.actor.kind === 'user' && e.actor.id ? (log.actors[e.actor.id] ?? 'A person') : e.actor.kind === 'device' ? 'A machine' : 'The platform';
  const what = (() => {
    switch (e.action) {
      case 'mesh.node.register':
        return `joined the mesh: ${node}${list(d.tags) !== 'no tags' ? ` with ${list(d.tags)}` : ''}`;
      case 'mesh.node.remove':
        return `took ${node} off the mesh for good`;
      case 'mesh.node.disable':
        return `turned ${node} off`;
      case 'mesh.node.enable':
        return `turned ${node} back on`;
      case 'mesh.node.rename':
        return `renamed ${text(d.from) ?? 'a machine'} to ${text(d.to) ?? node}`;
      case 'mesh.node.tags':
        return `changed the tags of ${node} from ${list(d.from)} to ${list(d.to)}`;
      case 'mesh.node.key-expiry':
        return d.keyExpiry === 'never' ? `turned key expiry off for ${node}` : `put key expiry back on for ${node}`;
      case 'mesh.key.expire':
        return `expired the key of ${node}, so its agent fetches a new one`;
      case 'mesh.key.rotate':
        return `${node} rotated its key`;
      case 'mesh.key.rejected':
        return `${node} was refused: its key was not accepted`;
      case 'mesh.key.expiring':
        return `the key of ${node} expires within 24 hours`;
      case 'mesh.key.expired':
        return `the key of ${node} expired`;
      case 'mesh.webhook.created':
        return `added a webhook to ${text(d.host) ?? 'an address'}`;
      case 'mesh.webhook.updated':
        return `changed the webhook to ${text(d.host) ?? 'an address'}`;
      case 'mesh.webhook.deleted':
        return `deleted the webhook to ${text(d.host) ?? 'an address'}`;
      case 'mesh.webhook.tested':
        return `sent a test event to ${text(d.host) ?? 'a webhook'}`;
      case 'mesh.webhook.disabled':
        return `turned off the webhook to ${text(d.host) ?? 'an address'} after repeated failures`;
      case 'mesh.flow.refused':
        return `${node} refused a connection the policy does not allow`;
      case 'mesh.flow-retention':
        return 'changed how long flow logs are kept';
      case 'mesh.policy.update':
        return d.restoredFrom ? 'restored an earlier access policy' : `published the access policy${Array.isArray(d.changed) && d.changed.length ? ` (changed ${d.changed.join(', ')})` : ''}`;
      default:
        return e.action.replace(/^mesh\./, '').replace(/[.-]/g, ' ');
    }
  })();
  return { who, what };
}

function EntryList({ log, empty }: { log: MeshLogResponse; empty: string }) {
  if (log.entries.length === 0) return <p className="px-4 py-3 text-sm text-muted">{empty}</p>;
  return (
    <ul className="divide-y divide-separator text-sm" role="list">
      {log.entries.map((e, i) => {
        const w = meshEntryInWords(e, log);
        return (
          <li key={e.seq ?? i} className="flex items-baseline justify-between gap-4 px-4 py-2.5">
            <span className="min-w-0">
              <span className="font-medium">{w.who}</span> {w.what}
              {e.outcome !== 'ok' && (
                <span className="ml-2 align-middle">
                  <StatusMark state="error" label="Refused" />
                </span>
              )}
            </span>
            <time className="shrink-0 whitespace-nowrap text-xs text-muted" dateTime={e.ts} title={dateTime(e.ts)}>
              {timeAgo(e.ts)}
            </time>
          </li>
        );
      })}
    </ul>
  );
}

/** What was done to one machine, and what it reported (its recent activity). */
export function NodeActivity({ nodeId }: { nodeId: string }) {
  const log = useLoad(() => routes.meshNodeActivity(nodeId), [nodeId]);
  return (
    <div className="grid gap-2">
      <ErrorNote error={log.error} />
      {log.data && <EntryList log={log.data} empty="Nothing recorded for this machine yet." />}
    </div>
  );
}

const SINCE = { hour: 'Last hour', day: 'Last 24 hours', week: 'Last 7 days', month: 'Last 30 days' } as const;
const SINCE_MS: Record<keyof typeof SINCE, number> = { hour: 3_600_000, day: 86_400_000, week: 7 * 86_400_000, month: 30 * 86_400_000 };

/** Every connection the mesh's agents saw, newest first. */
function FlowLog({ tone: _tone, nodeId, nodeName }: { tone: ArtTone; nodeId?: string; nodeName?: string }) {
  const [refusedOnly, setRefusedOnly] = useState(false);
  const [since, setSince] = useState<keyof typeof SINCE>('day');
  const flows = useLoad(() => routes.meshFlows({ ...(nodeId ? { nodeId } : {}), since: new Date(Date.now() - SINCE_MS[since]).toISOString(), ...(refusedOnly ? { refused: true } : {}), limit: 200 }), [nodeId, since, refusedOnly], 60_000);
  const rows: MeshFlowView[] = flows.data?.flows ?? [];
  const total = flows.data?.total ?? 0;
  return (
    <ListGroup title="Network flow logs" count={flows.data ? total : undefined} description={total > rows.length ? `The newest ${rows.length} of ${total}` : nodeName ? `Connections ${nodeName} accepted, refused or opened` : 'Connections the machines accepted, refused or opened'} actions={
        <div className="flex flex-wrap items-center gap-2">
          <Segment aria-label="Which connections" size="sm" selectedKey={refusedOnly ? 'refused' : 'all'} onSelectionChange={(k) => setRefusedOnly(String(k) === 'refused')} className="w-fit">
            <Segment.Item id="all">All</Segment.Item>
            <Segment.Item id="refused">Refused</Segment.Item>
          </Segment>
          <ChoiceSelect label="Since" value={since} options={SINCE} onChange={setSince} className="w-40" />
        </div>
      }><ListBlock flush>
      <ErrorNote error={flows.error} />
      {rows.length === 0 && !flows.loading ? (
        <p className="px-4 py-3 text-sm text-muted">{refusedOnly ? 'Nothing refused in this time.' : 'No connections in this time. The mesh agents send what they see every minute.'}</p>
      ) : (
        <table className="w-full table-fixed text-sm">
          <thead className="text-left text-xs text-muted">
            <tr>
              <th className="px-4 py-2 font-medium">From and to</th>
              <th className="w-24 px-4 py-2 font-medium md:w-40">Port</th>
              <th className="hidden w-44 px-4 py-2 font-medium md:table-cell">Traffic</th>
              <th className="w-24 px-4 py-2 font-medium md:w-28">When</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((f) => {
              const a = f.nodeName ?? f.nodeId;
              const b = f.peerName ?? f.peerNodeId;
              return (
                <tr key={f.id} className="border-t border-separator">
                  <td className="px-4 py-2">
                    <span className="block truncate" title={f.peerDnsName}>
                      {f.direction === 'in' ? `${b} to ${a}` : `${a} to ${b}`}
                    </span>
                    {f.via === 'relay' && <span className="block text-xs text-muted">Relayed</span>}
                  </td>
                  <td className="px-4 py-2">
                    <span className="mono tabular-nums">{f.port}</span>
                    {f.refused && (
                      <span className="block">
                        <StatusMark state="error" label="Refused" />
                      </span>
                    )}
                  </td>
                  <td className="hidden px-4 py-2 text-xs text-muted tabular-nums md:table-cell">{f.refused ? '—' : `${bytesInWords(f.bytesIn)} in, ${bytesInWords(f.bytesOut)} out`}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-muted">{timeAgo(f.startedAt)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </ListBlock></ListGroup>
  );
}

/** The Logs tab: configuration logs and network flow logs. */
export function MeshLogs({ tone }: { tone: ArtTone }) {
  const [part, setPart] = useState<'config' | 'flows'>('config');
  const log = useLoad(() => routes.meshLog(200), [], 60_000);
  return (
    <div className="grid gap-4">
      <Segment aria-label="Which log" size="sm" selectedKey={part} onSelectionChange={(k) => setPart(String(k) === 'flows' ? 'flows' : 'config')} className="w-fit">
        <Segment.Item id="config">Configuration logs</Segment.Item>
        <Segment.Item id="flows">Network flow logs</Segment.Item>
      </Segment>
      {part === 'config' ? (
        <ListGroup title="Configuration logs" count={log.data?.entries.length} description="Every change to the mesh: who made it, and when. The audit trail keeps them for good."><ListBlock flush>
          <ErrorNote error={log.error} />
          {log.data && <EntryList log={log.data} empty="Nothing has been changed on the mesh yet." />}
        </ListBlock></ListGroup>
      ) : (
        <FlowLog tone={tone} />
      )}
    </div>
  );
}

export { FlowLog };
